import type { TableProps } from "../../../utils/local-antd";
import { TFunction } from "i18next";

import { EntriesTableSettings } from "../../../model/entries-table-settings/EntriesTableSettings";
import { Entry } from "../../../model/entry/Entry";
import { getTableCellClassName } from "./getTableCellClassName";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { User } from "../../../model/user/User";
import { AssigneeBanner } from "../../../components/AssigneeBanner";
import { TableActionableCellEmail } from "../../../components/table/TableActionableCellEmail";
import { TableActionableCellPhone } from "../../../components/table/TableActionableCellPhone";

type FieldFunctions = {
  t: TFunction;
};

const getFieldWidth = (field: string) => {
  switch (true) {
    case field === "actions":
      return 80;
    case field === "task":
    case field === "assignee":
      return 140;
    case field === "phone" || field.startsWith("custom-field-"):
      return 250;
    case field === "email" || field === "description":
      return 375;
    default:
      return 200;
  }
};

const getFieldInfo = (
  column: EntriesTableSettings["columns"][number],
  functions: { t: TFunction },
  members?: User[],
) => {
  const { t } = functions;
  const { field } = column;

  switch (true) {
    case field === "assignee":
      return {
        title: t("common.assignees"),
        key: "assignee",
        render: (entry: Entry) => (
          <AssigneeBanner
            assignees={members?.filter((m) =>
              entry?.assigneeIds?.includes(m._id),
            )}
          />
        ),
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(field),
          },
        }),
      };

    case field === "phone":
      return {
        title: t("common.phone"),
        key: "phone",
        render: ({ fields, kind }: Entry) => {
          return (
            <TableActionableCellPhone phone={fields?.phone} extra={{ kind }} />
          );
        },
        sortable: true,
        reorder: true,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(field),
          },
        }),
        data: column,
      };

    case field === "email":
      return {
        title: t("common.email"),
        key: "email",
        render: ({ fields, kind }: Entry) => {
          return {
            props: { className: getTableCellClassName(kind) },
            children: (
              <TableActionableCellEmail
                email={fields?.email}
                extra={{ kind }}
              />
            ),
          };
        },
        sortable: true,
        reorder: true,
        data: column,
        onCell: () => ({
          style: {
            minWidth: getFieldWidth(field),
          },
        }),
      };

    case ["actions", "task", "tags", "form", "description"].includes(field) ||
      field.startsWith("custom-field-"):
      return { data: { enabled: false } };

    default:
      return {
        title: column.custom ? column.label : t(`common.${field}`),
        key: field,
        render: (entry: Entry) => {
          const value =
            (
              entry[field as keyof Entry] ||
              entry.fields?.[field as keyof Entry["fields"]]
            )?.toString() || "";

          return <TextWithTruncated length={20}>{value}</TextWithTruncated>;
        },
        sortable: true,
        reorder: true,
        width: getFieldWidth(field),
        data: column,
      };
  }
};

export function createEntriesWidgetTableColumns<T>(
  columns: EntriesTableSettings["columns"],
  functions: FieldFunctions,
  members?: User[],
) {
  return columns.map((c) =>
    getFieldInfo(c, functions, members),
  ) as TableProps<T>["columns"];
}
